/**
 * Responsive Table Component
 * 
 * Features:
 * - Horizontal scroll on mobile
 * - Sticky header
 * - Column visibility toggle
 * - Sort functionality
 * - Mobile card view option
 * 
 * Version: 6.2.0
 */
import React, { useState, ReactNode } from 'react'
import './ResponsiveTable.css'

export interface Column<T> {
  key: keyof T | string
  header: string
  width?: string
  sortable?: boolean
  render?: (value: T[keyof T], row: T) => ReactNode
  hideOnMobile?: boolean
}

interface ResponsiveTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyField: keyof T
  emptyMessage?: string
  loading?: boolean
  striped?: boolean
  hoverable?: boolean
  cardViewOnMobile?: boolean
  className?: string
  onRowClick?: (row: T) => void
}

function ResponsiveTable<T extends Record<string, unknown>>({
  columns,
  data,
  keyField,
  emptyMessage = 'No data available',
  loading = false,
  striped = true,
  hoverable = true,
  cardViewOnMobile = true,
  className = '',
  onRowClick
}: ResponsiveTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())
  const [showColumnToggle, setShowColumnToggle] = useState(false)

  // Filter visible columns
  const visibleColumns = columns.filter(col => !hiddenColumns.has(col.key as string))

  // Sort data
  const sortedData = [...data].sort((a, b) => {
    if (!sortKey) return 0
    const aVal = a[sortKey as keyof T]
    const bVal = b[sortKey as keyof T]
    
    if (aVal === bVal) return 0
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1
    
    const comparison = aVal < bVal ? -1 : 1
    return sortDirection === 'asc' ? comparison : -comparison
  })

  // Handle sort
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  // Toggle column visibility
  const toggleColumn = (key: string) => {
    const newHidden = new Set(hiddenColumns)
    if (newHidden.has(key)) {
      newHidden.delete(key)
    } else {
      newHidden.add(key)
    }
    setHiddenColumns(newHidden)
  }

  // Render cell value
  const renderCell = (column: Column<T>, row: T) => {
    const value = row[column.key as keyof T]
    if (column.render) {
      return column.render(value, row)
    }
    return String(value ?? '-')
  }

  if (loading) {
    return (
      <div className="responsive-table-wrapper">
        <div className="table-loading">
          <div className="loading-spinner" />
          <span>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`responsive-table-container ${className}`}>
      {/* Column Toggle Button */}
      <div className="table-controls">
        <button 
          className="column-toggle-btn"
          onClick={() => setShowColumnToggle(!showColumnToggle)}
        >
          ☰ Columns
        </button>
        
        {/* Column Toggle Dropdown */}
        {showColumnToggle && (
          <div className="column-dropdown">
            <div className="column-dropdown-header">
              <span>Toggle Columns</span>
              <button onClick={() => setShowColumnToggle(false)}>✕</button>
            </div>
            <div className="column-list">
              {columns.map(col => (
                <label key={col.key as string} className="column-item">
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(col.key as string)}
                    onChange={() => toggleColumn(col.key as string)}
                  />
                  <span>{col.header}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="responsive-table-wrapper">
        <table className={`responsive-table ${striped ? 'striped' : ''} ${hoverable ? 'hoverable' : ''}`}>
          <thead>
            <tr>
              {visibleColumns.map(col => (
                <th 
                  key={col.key as string}
                  style={{ width: col.width }}
                  className={col.sortable ? 'sortable' : ''}
                  onClick={() => col.sortable && handleSort(col.key as string)}
                >
                  <div className="th-content">
                    <span>{col.header}</span>
                    {col.sortable && sortKey === col.key && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="empty-cell">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr 
                  key={String(row[keyField])}
                  onClick={() => onRowClick?.(row)}
                  className={onRowClick ? 'clickable' : ''}
                >
                  {visibleColumns.map(col => (
                    <td key={col.key as string}>
                      {renderCell(col, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      {cardViewOnMobile && (
        <div className="mobile-card-view">
          {sortedData.length === 0 ? (
            <div className="empty-cards">{emptyMessage}</div>
          ) : (
            sortedData.map(row => (
              <div 
                key={String(row[keyField])} 
                className="table-card"
                onClick={() => onRowClick?.(row)}
              >
                {visibleColumns.map(col => (
                  <div key={col.key as string} className="card-row">
                    <span className="card-label">{col.header}</span>
                    <span className="card-value">{renderCell(col, row)}</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default ResponsiveTable